import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Users,
    MessageSquare,
    Image,
    HardDrive,
    Cpu,
    ArrowRight,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import { formatBytes } from '../../lib/utils';

interface Stats {
    users: number;
    chats: number;
    messages: number;
    media_files: number;
    media_size_bytes: number;
}

export default function AdminDashboard() {
    const [stats, setStats] = useState<Stats | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const response = await adminApi.getStats();
            if (response.success) {
                setStats(response.stats);
            }
        } catch (error) {
            console.error('Failed to load stats:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const statCards = stats
        ? [
            {
                label: '전체 사용자',
                value: stats.users,
                icon: Users,
                color: 'text-accent-400 bg-accent-500/10',
                link: '/admin/users',
            },
            {
                label: '채팅 세션',
                value: stats.chats,
                icon: MessageSquare,
                color: 'text-green-400 bg-green-500/10',
            },
            {
                label: '메시지',
                value: stats.messages.toLocaleString(),
                icon: MessageSquare,
                color: 'text-purple-400 bg-purple-500/10',
            },
            {
                label: '미디어 파일',
                value: stats.media_files,
                icon: Image,
                color: 'text-yellow-400 bg-yellow-500/10',
            },
            {
                label: '스토리지 사용량',
                value: formatBytes(stats.media_size_bytes),
                icon: HardDrive,
                color: 'text-pink-400 bg-pink-500/10',
            },
        ]
        : [];

    const quickLinks = [
        {
            label: '사용자 관리',
            description: '사용자 생성, 삭제, 비밀번호 재설정',
            icon: Users,
            link: '/admin/users',
        },
        {
            label: '모델 설정',
            description: 'AI 모델 활성화 및 관리',
            icon: Cpu,
            link: '/admin/models',
        },
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-dark-100">Admin Dashboard</h1>
                <p className="text-dark-400 mt-1">시스템 현황 및 관리</p>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="spinner text-accent-500 w-8 h-8" />
                </div>
            ) : (
                <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
                        {statCards.map((stat, index) => (
                            <div
                                key={index}
                                className="card flex items-center gap-4"
                            >
                                <div
                                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}
                                >
                                    <stat.icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-dark-100">{stat.value}</p>
                                    <p className="text-sm text-dark-400">{stat.label}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Links */}
                    <h2 className="text-lg font-semibold text-dark-100 mb-4">빠른 링크</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {quickLinks.map((link, index) => (
                            <Link
                                key={index}
                                to={link.link}
                                className="card-hover flex items-center justify-between p-5"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-dark-800 flex items-center justify-center">
                                        <link.icon className="w-6 h-6 text-dark-300" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-dark-100">{link.label}</h3>
                                        <p className="text-sm text-dark-400">{link.description}</p>
                                    </div>
                                </div>
                                <ArrowRight className="w-5 h-5 text-dark-500" />
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
